import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Plus,
	Search,
	Filter,
	Eye,
	Edit2,
	Trash2,
	MoreVertical,
	FolderOpen,
	Calendar,
	Users,
	CheckCircle,
	Clock,
	AlertCircle,
	LayoutGrid,
	List,
	RefreshCw,
} from 'lucide-react'
import { Button, DataTable, Modal, ConfirmDialog, Alert, Select, Input } from '@components/common'
import { ROLES, hasPermission } from '@config/roles'
import projectService from '@services/projectService'
import './Projects.scss'

/**
 * Projects Page Component
 *
 * Displays all projects for the company with filtering, search,
 * and view toggle (grid/table) capabilities.
 *
 * @param {Object} props
 * @param {string} props.userRole - Current user's role
 */
const Projects = ({ userRole = ROLES.COMPANY_ADMIN }) => {
	const navigate = useNavigate()

	// State
	const [projects, setProjects] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [viewMode, setViewMode] = useState('table') // 'grid' or 'table' - default to table for better data overview
	const [searchQuery, setSearchQuery] = useState('')
	const [statusFilter, setStatusFilter] = useState('')
	const [priorityFilter, setPriorityFilter] = useState('')
	const [typeFilter, setTypeFilter] = useState('')
	const [selectedProject, setSelectedProject] = useState(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	// Permissions
	const canCreateProject = hasPermission(userRole, 'canManageProjects')
	const canEditProject = hasPermission(userRole, 'canManageProjects') || hasPermission(userRole, 'canManageAssignedProjects')
	const canDeleteProject = hasPermission(userRole, 'canManageProjects')

	// Filter options
	const statusOptions = [
		{ value: '', label: 'All Status' },
		{ value: 'planning', label: 'Planning' },
		{ value: 'in_progress', label: 'In Progress' },
		{ value: 'review', label: 'Under Review' },
		{ value: 'completed', label: 'Completed' },
		{ value: 'on_hold', label: 'On Hold' },
		{ value: 'cancelled', label: 'Cancelled' },
	]

	const priorityOptions = [
		{ value: '', label: 'All Priority' },
		{ value: 'critical', label: 'Critical' },
		{ value: 'high', label: 'High' },
		{ value: 'medium', label: 'Medium' },
		{ value: 'low', label: 'Low' },
	]

	const typeOptions = [
		{ value: '', label: 'All Types' },
		{ value: 'certification', label: 'Certification' },
		{ value: 'audit', label: 'Audit' },
		{ value: 'policy', label: 'Policy' },
		{ value: 'assessment', label: 'Assessment' },
		{ value: 'training', label: 'Training' },
	]

	// Fetch projects
	useEffect(() => {
		fetchProjects()
	}, [])

	const fetchProjects = async () => {
		setLoading(true)
		setError(null)
		try {
			// SECURITY: company_id is now automatically filtered by backend
			// No need to pass company_id parameter - backend uses authenticated user's company
			const response = await projectService.getAll()
			if (response.success || response.data) {
				setProjects(response.data || response)
			} else {
				setError('Failed to load projects')
			}
		} catch (err) {
			console.error('Error fetching projects:', err)
			setError('Failed to load projects. Please try again.')
		} finally {
			setLoading(false)
		}
	}

	// Filter and search projects
	const filteredProjects = useMemo(() => {
		return projects.filter((project) => {
			// Search filter
			if (searchQuery) {
				const query = searchQuery.toLowerCase()
				const matchesName = project.name?.toLowerCase().includes(query)
				const matchesDescription = project.description?.toLowerCase().includes(query)
				if (!matchesName && !matchesDescription) return false
			}

			// Status filter
			if (statusFilter && project.status !== statusFilter) return false

			// Priority filter
			if (priorityFilter && project.priority !== priorityFilter) return false

			// Type filter
			if (typeFilter && project.type !== typeFilter) return false

			return true
		})
	}, [projects, searchQuery, statusFilter, priorityFilter, typeFilter])

	// Statistics
	const stats = useMemo(() => {
		const total = projects.length
		const inProgress = projects.filter((p) => p.status === 'in_progress').length
		const completed = projects.filter((p) => p.status === 'completed').length
		const atRisk = projects.filter((p) => p.priority === 'critical' || p.priority === 'high').length
		return { total, inProgress, completed, atRisk }
	}, [projects])

	// Handlers
	const handleCreateProject = () => {
		navigate('/projects/new')
	}

	const handleViewProject = (project) => {
		// Navigate to project detail page
		navigate(`/projects/${project.id}`)
	}

	const handleEditProject = (project) => {
		// Navigate to edit project page (to be implemented)
		navigate(`/projects/${project.id}/edit`)
	}

	const handleDeleteClick = (project) => {
		setSelectedProject(project)
		setShowDeleteConfirm(true)
	}

	const handleDeleteConfirm = async () => {
		if (!selectedProject) return

		setActionLoading(true)
		try {
			await projectService.delete(selectedProject.id)
			setProjects(projects.filter((p) => p.id !== selectedProject.id))
			setShowDeleteConfirm(false)
			setSelectedProject(null)
		} catch (err) {
			console.error('Error deleting project:', err)
			setError('Failed to delete project. Please try again.')
		} finally {
			setActionLoading(false)
		}
	}

	const handleStatusChange = async (project, newStatus) => {
		try {
			await projectService.updateStatus(project.id, newStatus)
			setProjects(projects.map((p) => (p.id === project.id ? { ...p, status: newStatus } : p)))
		} catch (err) {
			console.error('Error updating status:', err)
			setError('Failed to update project status.')
		}
	}

	const clearFilters = () => {
		setSearchQuery('')
		setStatusFilter('')
		setPriorityFilter('')
		setTypeFilter('')
	}

	// Get status display info
	const getStatusInfo = (status) => {
		const statusMap = {
			planning: { label: 'Planning', className: 'status--info', icon: Clock },
			in_progress: { label: 'In Progress', className: 'status--primary', icon: RefreshCw },
			review: { label: 'Under Review', className: 'status--warning', icon: Eye },
			completed: { label: 'Completed', className: 'status--success', icon: CheckCircle },
			on_hold: { label: 'On Hold', className: 'status--secondary', icon: AlertCircle },
			cancelled: { label: 'Cancelled', className: 'status--danger', icon: AlertCircle },
		}
		return statusMap[status] || { label: status, className: 'status--secondary', icon: Clock }
	}

	// Get priority display info
	const getPriorityInfo = (priority) => {
		const priorityMap = {
			critical: { label: 'Critical', className: 'priority--critical' },
			high: { label: 'High', className: 'priority--high' },
			medium: { label: 'Medium', className: 'priority--medium' },
			low: { label: 'Low', className: 'priority--low' },
		}
		return priorityMap[priority] || { label: priority, className: 'priority--medium' }
	}

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})
	}

	// Table columns configuration
	const tableColumns = [
		{
			key: 'name',
			label: 'Project Name',
			sortable: true,
			render: (row) => (
				<div className="project-name-cell">
					<div className="project-name-cell__icon">
						<FolderOpen size={18} />
					</div>
					<div className="project-name-cell__info">
						<span className="project-name-cell__name">{row.name}</span>
						{row.description && <span className="project-name-cell__desc">{row.description.substring(0, 60)}...</span>}
					</div>
				</div>
			),
		},
		{
			key: 'type',
			label: 'Type',
			sortable: true,
			render: (row) => <span className="project-type">{row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1) : '-'}</span>,
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			render: (row) => {
				const statusInfo = getStatusInfo(row.status)
				return <span className={`project-status ${statusInfo.className}`}>{statusInfo.label}</span>
			},
		},
		{
			key: 'priority',
			label: 'Priority',
			sortable: true,
			render: (row) => {
				const priorityInfo = getPriorityInfo(row.priority)
				return <span className={`project-priority ${priorityInfo.className}`}>{priorityInfo.label}</span>
			},
		},
		{
			key: 'progress',
			label: 'Progress',
			sortable: true,
			render: (row) => {
				const progress = row.progress || 0
				// Determine color class based on progress
				const progressClass = progress === 0 ? 'zero' : progress < 30 ? 'low' : progress < 70 ? 'medium' : 'high'
				// Determine bar color
				const barColor =
					progress === 0
						? '#d1d5db'
						: progress < 30
							? 'linear-gradient(90deg, #ef4444, #f87171)'
							: progress < 70
								? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
								: 'linear-gradient(90deg, #10b981, #34d399)'

				return (
					<div className={`progress-cell progress-cell--${progressClass}`}>
						<div className="progress-bar">
							<div className="progress-bar__fill" style={{ width: `${progress}%`, background: barColor }}></div>
						</div>
						<span className={`progress-cell__percent progress-cell__percent--${progressClass}`}>{progress}%</span>
					</div>
				)
			},
		},
		{
			key: 'target_date',
			label: 'Due Date',
			sortable: true,
			render: (row) => {
				const hasDate = row.target_date && row.target_date !== 'null' && row.target_date !== ''
				return (
					<span className={`project-date ${!hasDate ? 'project-date--empty' : ''}`}>
						{hasDate ? formatDate(row.target_date) : 'Not Set'}
					</span>
				)
			},
		},
	]

	// Table actions - Icon-only with tooltips for cleaner UI
	const tableActions = [
		{
			label: 'View',
			icon: <Eye size={16} />,
			onClick: (row) => handleViewProject(row),
			variant: 'primary',
			title: 'View project details',
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: (row) => handleEditProject(row),
			variant: 'default',
			show: () => canEditProject,
			title: 'Edit project',
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: (row) => handleDeleteClick(row),
			variant: 'danger',
			show: () => canDeleteProject,
			title: 'Delete project',
		},
	]

	// Render project card
	const renderProjectCard = (project) => {
		const statusInfo = getStatusInfo(project.status)
		const priorityInfo = getPriorityInfo(project.priority)
		const StatusIcon = statusInfo.icon

		return (
			<div key={project.id} className="project-card" onClick={() => handleViewProject(project)}>
				{/* Card Header with Title and Actions */}
				<div className="project-card__header">
					<div className="project-card__title-row">
						<div className="project-card__icon">
							<FolderOpen size={20} />
						</div>
						<h3 className="project-card__title">{project.name}</h3>
					</div>
					<div className="project-card__actions">
						{canEditProject && (
							<button
								className="project-card__action-btn"
								onClick={(e) => {
									e.stopPropagation()
									handleEditProject(project)
								}}
								title="Edit"
							>
								<Edit2 size={16} />
							</button>
						)}
						{canDeleteProject && (
							<button
								className="project-card__action-btn project-card__action-btn--danger"
								onClick={(e) => {
									e.stopPropagation()
									handleDeleteClick(project)
								}}
								title="Delete"
							>
								<Trash2 size={16} />
							</button>
						)}
					</div>
				</div>

				{/* Card Body */}
				<div className="project-card__body">
					{project.description && (
						<p className="project-card__description">
							{project.description.length > 100 ? `${project.description.substring(0, 100)}...` : project.description}
						</p>
					)}

					<div className="project-card__badges">
						<span className={`project-status ${statusInfo.className}`}>
							<StatusIcon size={12} />
							{statusInfo.label}
						</span>
						<span className={`project-priority ${priorityInfo.className}`}>{priorityInfo.label}</span>
					</div>
				</div>

				{/* Card Footer */}
				<div className="project-card__footer">
					<div className="project-card__progress">
						<div className="progress-bar">
							<div className="progress-bar__fill" style={{ width: `${project.progress || 0}%` }}></div>
						</div>
						<span className="project-card__progress-text">{project.progress || 0}%</span>
					</div>

					<div className="project-card__meta">
						<span className="project-card__date">
							<Calendar size={14} />
							{formatDate(project.target_date)}
						</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="projects-page">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__left">
					<h1 className="page-title">Projects</h1>
					<nav className="breadcrumb">
						<span className="breadcrumb__item">Dashboard</span>
						<span className="breadcrumb__separator">/</span>
						<span className="breadcrumb__item breadcrumb__item--active">Projects</span>
					</nav>
				</div>
				{canCreateProject && (
					<div className="page-header__right">
						<Button variant="primary" onClick={handleCreateProject}>
							<Plus size={18} />
							New Project
						</Button>
					</div>
				)}
			</div>

			{/* Error Alert */}
			{error && (
				<Alert type="error" onClose={() => setError(null)} className="projects-page__alert">
					{error}
				</Alert>
			)}

			{/* Stats Cards */}
			<div className="stats-row">
				<div className="stat-card stat-card--total">
					<div className="stat-card__header">
						<div className="stat-card__info">
							<h3 className="stat-card__title">Total Projects</h3>
							<p className="stat-card__value">{stats.total}</p>
						</div>
						<div className="stat-card__icon">
							<FolderOpen size={24} />
						</div>
					</div>
				</div>

				<div className="stat-card stat-card--in-progress">
					<div className="stat-card__header">
						<div className="stat-card__info">
							<h3 className="stat-card__title">In Progress</h3>
							<p className="stat-card__value">{stats.inProgress}</p>
						</div>
						<div className="stat-card__icon">
							<RefreshCw size={24} />
						</div>
					</div>
				</div>

				<div className="stat-card stat-card--completed">
					<div className="stat-card__header">
						<div className="stat-card__info">
							<h3 className="stat-card__title">Completed</h3>
							<p className="stat-card__value">{stats.completed}</p>
						</div>
						<div className="stat-card__icon">
							<CheckCircle size={24} />
						</div>
					</div>
				</div>

				<div className="stat-card stat-card--at-risk">
					<div className="stat-card__header">
						<div className="stat-card__info">
							<h3 className="stat-card__title">High Priority</h3>
							<p className="stat-card__value">{stats.atRisk}</p>
						</div>
						<div className="stat-card__icon">
							<AlertCircle size={24} />
						</div>
					</div>
				</div>
			</div>

			{/* Filters & Search Bar */}
			<div className="projects-toolbar">
				<div className="search-box">
					<Search size={18} className="search-box__icon" />
					<input
						type="text"
						placeholder="Search projects..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="search-box__input"
					/>
				</div>

				<div className="filter-group">
					<Select
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={statusOptions}
						placeholder="Status"
						className="filter-select"
					/>
					<Select
						value={priorityFilter}
						onChange={(e) => setPriorityFilter(e.target.value)}
						options={priorityOptions}
						placeholder="Priority"
						className="filter-select"
					/>
					<Select
						value={typeFilter}
						onChange={(e) => setTypeFilter(e.target.value)}
						options={typeOptions}
						placeholder="Type"
						className="filter-select"
					/>

					{(searchQuery || statusFilter || priorityFilter || typeFilter) && (
						<button className="clear-filters-btn" onClick={clearFilters}>
							Clear
						</button>
					)}
				</div>

				<div className="projects-toolbar__actions">
					<div className="view-toggle">
						<button className={`view-toggle__btn ${viewMode === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewMode('grid')} title="Grid View">
							<LayoutGrid size={18} />
						</button>
						<button className={`view-toggle__btn ${viewMode === 'table' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewMode('table')} title="Table View">
							<List size={18} />
						</button>
					</div>

					<button className="refresh-btn" onClick={fetchProjects} title="Refresh">
						<RefreshCw size={18} />
					</button>
				</div>
			</div>

			{/* Projects Content */}
			<div className="projects-content">
				{loading ? (
					<div className="projects-loading">
						<div className="loading-spinner"></div>
						<p>Loading projects...</p>
					</div>
				) : filteredProjects.length === 0 ? (
					<div className="projects-empty">
						<FolderOpen size={64} className="projects-empty__icon" />
						<h3>No Projects Found</h3>
						<p>{searchQuery || statusFilter || priorityFilter || typeFilter ? 'Try adjusting your filters or search query.' : 'Get started by creating your first project.'}</p>
						{canCreateProject && !searchQuery && !statusFilter && !priorityFilter && !typeFilter && (
							<Button variant="primary" onClick={handleCreateProject}>
								<Plus size={18} />
								Create Project
							</Button>
						)}
					</div>
				) : viewMode === 'grid' ? (
					<div className="projects-grid">{filteredProjects.map(renderProjectCard)}</div>
				) : (
					<div className="projects-table">
						<DataTable columns={tableColumns} data={filteredProjects} actions={tableActions} loading={loading} emptyMessage="No projects found" onRowClick={handleViewProject} />
					</div>
				)}
			</div>

			{/* View Project Modal */}
			<Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="Project Details" size="large" closeOnBackdrop={false}>
				{selectedProject && (
					<div className="project-details">
						<div className="project-details__header">
							<div className="project-details__icon">
								<FolderOpen size={32} />
							</div>
							<div className="project-details__title-group">
								<h2>{selectedProject.name}</h2>
								<div className="project-details__badges">
									<span className={`project-status ${getStatusInfo(selectedProject.status).className}`}>{getStatusInfo(selectedProject.status).label}</span>
									<span className={`project-priority ${getPriorityInfo(selectedProject.priority).className}`}>{getPriorityInfo(selectedProject.priority).label}</span>
								</div>
							</div>
						</div>

						{selectedProject.description && (
							<div className="project-details__section">
								<h4>Description</h4>
								<p>{selectedProject.description}</p>
							</div>
						)}

						<div className="project-details__grid">
							<div className="project-details__item">
								<label>Type</label>
								<span>{selectedProject.type ? selectedProject.type.charAt(0).toUpperCase() + selectedProject.type.slice(1) : '-'}</span>
							</div>
							<div className="project-details__item">
								<label>Progress</label>
								<div className="progress-cell">
									<div className="progress-bar">
										<div className="progress-bar__fill" style={{ width: `${selectedProject.progress || 0}%` }}></div>
									</div>
									<span>{selectedProject.progress || 0}%</span>
								</div>
							</div>
							<div className="project-details__item">
								<label>Start Date</label>
								<span>{formatDate(selectedProject.start_date)}</span>
							</div>
							<div className="project-details__item">
								<label>Target Date</label>
								<span>{formatDate(selectedProject.target_date)}</span>
							</div>
							{selectedProject.industry && (
								<div className="project-details__item">
									<label>Industry</label>
									<span>{selectedProject.industry}</span>
								</div>
							)}
							{selectedProject.domain && (
								<div className="project-details__item">
									<label>Domain</label>
									<span>{selectedProject.domain}</span>
								</div>
							)}
						</div>

						<div className="project-details__actions">
							<Button variant="outline" onClick={() => setShowViewModal(false)}>
								Close
							</Button>
							{canEditProject && (
								<Button
									variant="primary"
									onClick={() => {
										setShowViewModal(false)
										handleEditProject(selectedProject)
									}}
								>
									<Edit2 size={16} />
									Edit Project
								</Button>
							)}
						</div>
					</div>
				)}
			</Modal>

			{/* Delete Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				onConfirm={handleDeleteConfirm}
				title="Delete Project"
				message={`Are you sure you want to delete "${selectedProject?.name}"? This action cannot be undone.`}
				confirmLabel="Delete"
				confirmVariant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}

export default Projects
